import './TabletInterface.css'
import {useAppStore} from '../stores/store.js'
import {useEffect, useRef, useState, useMemo} from 'react'
import {gsap} from 'gsap'
import {ALBUM_COVERS, ALBUM_GRADIENTS, SONG_NAMES, SONGS_FILEPATHS} from '../utils/constants.jsx'
import {
    MdPause,
    MdPlayArrow,
    MdSkipNext,
    MdSkipPrevious,
    MdVolumeOff,
    MdVolumeUp,
    MdVolumeDownAlt
} from 'react-icons/md'
import {shuffleArray} from '../utils/shuffleArray.jsx'

const MARQUEE_SPEED_PPS = 35 // Adjust speed as needed

export function TabletInterface() {

    const tabletRef = useRef(null)
    const closeTabletAction = useAppStore((state) => state.closeTablet)

    const selectedSong = useAppStore((state) => state.selectedSong)
    const nextSongAction = useAppStore((state) => state.nextSong)
    const previousSongAction = useAppStore((state) => state.previousSong)
    const isTabletMinimized = useAppStore((state) => state.isTabletMinimized)
    const minimizeTabletAction = useAppStore((state) => state.toggleMinimizeTablet)

    const [isPlaying, setIsPlaying] = useState(false)
    const [isReadyToPlay, setIsReadyToPlay] = useState(false)

    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)

    const audioRef = useRef(null)
    const playAfterLoadRef = useRef(false)
    const contentAreaRef = useRef(null) // Ref for the .tablet-content-area
    const imageRef = useRef(null) // Ref for the image
    const rotationTweenRef = useRef(null)
    const shadowTweenRef = useRef(null)

    const [isTitleOverflowing, setIsTitleOverflowing] = useState(false)
    const titleContainerRef = useRef(null)
    const titleMeasureRef = useRef(null)
    const [marqueeDuration, setMarqueeDuration] = useState(0)
    const [volume, setVolume] = useState(0.5)

    const handleToggleMute = () => {
        if (audioRef.current) {
            if (audioRef.current.volume > 0) {
                setVolume(0)
            } else {
                setVolume(0.5)
            }
        }
    }

    const handleOpenTablet = () => {
        if (tabletRef.current) {
            const timeline = gsap.timeline({
                defaults: {duration: 0.75, ease: 'power3.inOut'},
            })
            timeline.to(tabletRef.current, {y: -600}, 0)
        }
    }

    const handleCloseTablet = () => {
        if (audioRef.current && !audioRef.current.paused) {
            audioRef.current.pause()
        }
        const timeline = gsap.timeline({
            defaults: {duration: 0.75, ease: 'power2.inOut'},
            onComplete: () => {
                if (closeTabletAction) closeTabletAction()
            }
        })
        timeline.to(tabletRef.current, {y: 600}, 0)
    }

    const handleToggleMinimizeTablet = () => {
        const timeline = gsap.timeline({
            defaults: {duration: 0.75, ease: 'power2.inOut'},
        })
        // Check if the tablet is already minimized
        const yTranslate = isTabletMinimized ? -600 : -75
        timeline.to(tabletRef.current, {y: yTranslate}, 0)
        minimizeTabletAction()
    }

    const handlePreviousClick = () => {
        // e.stopPropagation()
        console.log('Previous song')
        // prevSongAction()
        playAfterLoadRef.current = isPlaying
        // setSelectedSong((prevIndex) => (prevIndex - 1 + TOTAL_SONGS) % TOTAL_SONGS)
        previousSongAction()
    }

    const handleNextClick = () => {
        // e.stopPropagation()
        console.log('Next song')
        // nextSongAction()
        playAfterLoadRef.current = isPlaying
        // setSelectedSong((prevIndex) => (prevIndex + 1) % TOTAL_SONGS)
        nextSongAction()
    }

    const handlePlayPause = () => {
        if (!audioRef.current || !isReadyToPlay) return // Don't do anything if audio isn't ready

        if (isPlaying) {
            audioRef.current.pause()
            playAfterLoadRef.current = false // Clear intent if user explicitly pauses
        } else {
            // Attempt to play
            audioRef.current.play().catch(error => {
                console.error('Audio play failed (user action):', error)
                // Usually fails due to browser restrictions if called without prior interaction
                setIsPlaying(false) // Ensure state is correct
            })
            // We don't set playAfterLoadRef here, rely on the actual play/pause events
        }
        // State (isPlaying) will be updated by the 'play'/'pause' event listeners
    }

    const handleTimeSliderChange = (event) => {
        if (!audioRef.current) return
        const newTime = Number(event.target.value)
        audioRef.current.currentTime = newTime
        // Update state immediately for responsive slider feel
        setCurrentTime(newTime)
    }

    // VOLUME CHECK
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume
        }
    }, [volume]) // Update audio element's volume when our volume state changes

    // Overflow check for title text
    useEffect(() => {
        const checkOverflow = () => {
            // Use the MEASUREMENT ref here
            if (titleContainerRef.current && titleMeasureRef.current) {
                const containerWidth = titleContainerRef.current.clientWidth
                // Measure the HIDDEN span which NEVER has padding
                const textWidth = titleMeasureRef.current.scrollWidth
                const isOverflowing = textWidth > containerWidth

                setIsTitleOverflowing(isOverflowing)

                if (isOverflowing) {
                    const duration = textWidth / MARQUEE_SPEED_PPS
                    setMarqueeDuration(duration)
                } else {
                    setMarqueeDuration(0)
                }
            } else {
                setIsTitleOverflowing(false)
                setMarqueeDuration(0)
            }
        }

        // Check immediately after render/song change
        // Use setTimeout to ensure DOM is updated if needed, though usually not required here
        // setTimeout(checkOverflow, 0)
        checkOverflow() // Usually sufficient

        window.addEventListener('resize', checkOverflow)
        return () => window.removeEventListener('resize', checkOverflow)

    }, [selectedSong])


    // --- ROTATION, BOX SHADOW ON PLAY ---
    useEffect(() => {
        console.log(`ROTATION/SHADOW EFFECT: Running. isPlaying: ${isPlaying}`)
        if (!imageRef.current || !contentAreaRef.current) {
            console.log('ROTATION/SHADOW EFFECT: Refs not ready.')
            return
        }

        // --- Manage Shadow Animation ---
        if (isPlaying) {
            // Start/Resume Shadow Pulse
            if (!shadowTweenRef.current) {
                console.log('Creating shadow tween')
                gsap.set(imageRef.current, {'--box-shadow': '0 0 20px 1px #ffffff'}) // Set start state
                shadowTweenRef.current = gsap.to(imageRef.current, {
                    '--box-shadow': '0 0 20px 10px #ffffff',
                    duration: 1, ease: 'power1.inOut', repeat: -1, yoyo: true,
                    paused: false, // Start unpaused
                    overwrite: 'auto'
                })
            } else {
                console.log('Resuming shadow tween')
                shadowTweenRef.current.resume()
            }
        } else { // isPlaying is false
            // Pause Shadow Pulse and set static state
            if (shadowTweenRef.current) {
                console.log('Pausing shadow tween')
                shadowTweenRef.current.pause()
                // Set the static state *after* pausing the animation
                gsap.set(imageRef.current, {
                    '--box-shadow': '0 0 20px 1px #ffffff'
                })
                console.log('Set static shadow.')
            } else {
                // If no tween exists (e.g., initial load paused), ensure static state is set
                gsap.to(imageRef.current, {
                    '--box-shadow': '0 0 20px 1px #ffffff', duration: 0.3
                })
                console.log('Set static shadow (no active tween found).')
            }
        }

        // --- Manage Rotation Animation ---
        if (isPlaying) {
            // Start or Resume Rotation
            if (!rotationTweenRef.current) {
                console.log('Creating rotation tween')
                rotationTweenRef.current = gsap.to(contentAreaRef.current, {
                    '--rotation-angle': '+=360deg',
                    duration: 20, ease: 'none', repeat: -1, overwrite: 'auto',
                    paused: false
                })
            } else {
                console.log('Resuming rotation tween')
                rotationTweenRef.current.resume()
            }
        } else { // isPlaying is false
            // PAUSE the rotation tween
            if (rotationTweenRef.current) {
                console.log('Pausing rotation tween.')
                rotationTweenRef.current.pause()
            } else {
                console.log('Rotation tween ref not found, cannot pause.')
            }
        }


        // Cleanup Function: Kill tweens on unmount
        return () => {
            console.log('ROTATION/SHADOW EFFECT: CLEANUP running.')
            if (shadowTweenRef.current) {
                shadowTweenRef.current.kill()
                shadowTweenRef.current = null
            }
            if (rotationTweenRef.current) {
                rotationTweenRef.current.kill()
                rotationTweenRef.current = null
            }
        }

    }, [isPlaying]) // Dependency: [isPlaying]


    // Open tablet animation
    useEffect(() => {
        if (tabletRef.current) {
            // Immediately set to closed state before animating open,
            // especially important if switching panel types.
            // gsap.set(tabletRef.current, {opacity: 0, scale: 0.85, pointerEvents: 'none'});
            handleOpenTablet(tabletRef.current)
        }
    }, [])

    // Audio events
    useEffect(() => {
        const audioElement = audioRef.current
        if (!audioElement) return // Exit if ref is not set yet

        const handleCanPlay = () => {
            console.log('Audio can play')
            setIsReadyToPlay(true)
            // If we intended to play this track after it loaded, play it now
            if (playAfterLoadRef.current) {
                audioElement.play().catch(e => console.error('Autoplay after load failed:', e))
                playAfterLoadRef.current = false // Reset the intent flag
            }
        }

        const handlePlay = () => {
            console.log('Audio playing')
            setIsPlaying(true)
        }

        const handlePause = () => {
            console.log('Audio paused')
            setIsPlaying(false)
        }

        const handleEnded = () => {
            console.log('Audio ended')
            setIsPlaying(false) // Set state to paused
            // Optional: Automatically play next song
            // playAfterLoadRef.current = true // Set intent for the *next* track
            // handleNext()
        }

        const handleLoadStart = () => {
            console.log('Audio load started')
            setIsReadyToPlay(false)
            setIsPlaying(false)
            // --- RESET TIME/DURATION ---
            setCurrentTime(0)
            setDuration(0)
        }

        // --- NEW Event Handlers ---
        const handleLoadedMetadata = () => {
            console.log('Audio metadata loaded')
            // Set duration only if it's a valid finite number
            setDuration(isFinite(audioElement.duration) ? audioElement.duration : 0)
        }

        const handleTimeUpdate = () => {
            // Update current time state as audio plays
            setCurrentTime(audioElement.currentTime)
        }

        const handleVolumeChangeOnElement = () => {
            if (audioElement) {
                // Sync our React state if the element's volume changes externally
                // This prevents a mismatch if, for example, browser media keys change volume
                // Be careful not to create an infinite loop if the browser fires this
                // event too eagerly when *we* set the volume.
                // A small threshold can help, or only update if significantly different.
                if (Math.abs(audioElement.volume - volume) > 0.01) {
                    setVolume(audioElement.volume)
                }
            }
        }

        // Add listeners
        audioElement.addEventListener('loadstart', handleLoadStart) // Fired when browser begins loading new src
        audioElement.addEventListener('canplay', handleCanPlay)   // Fired when enough data is loaded to potentially play
        audioElement.addEventListener('play', handlePlay)
        audioElement.addEventListener('pause', handlePause)
        audioElement.addEventListener('ended', handleEnded)

        audioElement.addEventListener('loadedmetadata', handleLoadedMetadata)
        audioElement.addEventListener('timeupdate', handleTimeUpdate)
        audioElement.addEventListener('volumechange', handleVolumeChangeOnElement)

        // Cleanup function
        return () => {
            console.log('Cleaning up audio listeners')
            audioElement.removeEventListener('loadstart', handleLoadStart)
            audioElement.removeEventListener('canplay', handleCanPlay)
            audioElement.removeEventListener('play', handlePlay)
            audioElement.removeEventListener('pause', handlePause)
            audioElement.removeEventListener('ended', handleEnded)

            audioElement.removeEventListener('loadedmetadata', handleLoadedMetadata)
            audioElement.removeEventListener('timeupdate', handleTimeUpdate)
            audioElement.removeEventListener('volumechange', handleVolumeChangeOnElement)
            // Optional: Pause audio if component unmounts while playing
            // if (!audioElement.paused) {
            //    audioElement.pause()
            // }
        }
    }, []) // Empty dependency array: Run once on mount, cleanup on unmount

    const handleVolumeSliderChange = (event) => {
        const newVolume = Number(event.target.value)
        setVolume(newVolume)
        // No need to directly set audioRef.current.volume here,
        // the useEffect [volume] will handle it.
    }

    // --- Effect to Handle Source Change ---
    // This effect ONLY runs when the selectedSong index changes.
    // Its job is *only* to update the audio element's source.
    // We rely on the 'loadstart' and 'canplay' events handled above
    // to manage the state and playback logic after the source changes.
    useEffect(() => {
        const audioElement = audioRef.current
        if (audioElement) {
            console.log(`Selected song changed to index ${selectedSong}, updating src to ${SONGS_FILEPATHS[selectedSong]}`)
            // Setting the src attribute implicitly triggers the browser's loading process
            audioElement.src = SONGS_FILEPATHS[selectedSong]
            // --- DO NOT call load() or play() here ---
        }
    }, [selectedSong]) // Re-run *only* when selectedSong changes

    // Background change effect on song change
    useEffect(() => {
        if (contentAreaRef.current && ALBUM_GRADIENTS[selectedSong]) {
            console.log(`Selected song changed to index ${selectedSong}, updating background gradient`)
            // --- Use GSAP for a smooth transition ---
            gsap.to(contentAreaRef.current, {
                '--gradient-image': ALBUM_GRADIENTS[selectedSong],
                duration: 0.5,
                ease: 'none',
                overwrite: 'auto' // <-- Add or ensure this is here
            })

        } else {
            console.warn(`Gradient not found for song index: ${selectedSong}`)
        }
        // No cleanup needed for this short tween


    }, [selectedSong]) // Dependency is [selectedSong]

    const scrambledTitle = useMemo(() => {
        const originalTitle = SONG_NAMES[selectedSong] || ""
        if (!originalTitle) {
            return ""
        }
        const originalChars = originalTitle.split("")
        const shuffledChars = shuffleArray(originalChars)
        return shuffledChars.join("")
    }, [selectedSong]) // Re-calculate only when selectedSong changes

    /*const scrambledTitle = useMemo(() => {
        return [...SONG_NAMES[selectedSong]].sort(() => Math.random() - .5).join('')
    }, [selectedSong]) // Re-calculate only when selectedSong changes*/

    const formatTime = (timeInSeconds) => {
        if (isNaN(timeInSeconds) || timeInSeconds === 0) return '0:00'
        const minutes = Math.floor(timeInSeconds / 60)
        const seconds = Math.floor(timeInSeconds % 60)
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
    }


    return (
        <div ref={tabletRef} className='tablet-interface'>
            <div ref={contentAreaRef} className='tablet-content-area'>
                <audio ref={audioRef} src={SONGS_FILEPATHS[selectedSong]} preload='metadata'>
                    Your browser does not support the audio element.
                </audio>
                <div className='tablet-top-buttons'>
                    <button className='tablet-action-button tablet-minimize-button'
                            onClick={handleToggleMinimizeTablet}><span className='minimize-icon-bar'></span></button>
                    <button className='tablet-action-button tablet-cross-button' onClick={handleCloseTablet}></button>
                </div>
                <div className='volume-and-image-container'>
                    {/* --- NEW: Volume Slider Container --- */}
                    <div className="volume-slider-container vertical">
                        {/* Optional: Volume Icon */}
                        <span className="volume-icon">
                            {
                                volume > 0.5 ? <MdVolumeUp onClick={handleToggleMute} className='volume-svg'/> :
                                    volume === 0 ? <MdVolumeOff onClick={handleToggleMute} className='volume-svg'/> :
                                        <MdVolumeDownAlt onClick={handleToggleMute} className='volume-svg'/>
                            }
                        </span>
                        <input
                            type="range"
                            className="volume-slider" // Add a class for styling
                            min="0"
                            max="1"
                            step="0.01" // Finer control for volume
                            value={volume}
                            onChange={handleVolumeSliderChange}
                            aria-label="Volume slider"
                            orient="vertical"
                        />
                    </div>
                    <img ref={imageRef} className='tablet-player-image' src={ALBUM_COVERS[selectedSong]} alt=''/>
                    <div className="volume-slider-container"></div>
                </div>
                <div ref={titleContainerRef} className='song-title-container'>
                    {/* Visible Title - gets animation */}
                    <div
                        className={`song-title-text ${isTitleOverflowing ? 'marquee' : ''}`}
                        style={isTitleOverflowing ? {animationDuration: `${marqueeDuration}s`} : {}}
                    >
                        {scrambledTitle}
                    </div>
                    {/* Hidden Measurement Span - NEVER gets animation/padding */}
                    <span ref={titleMeasureRef} className='song-title-measurement'>
                         {SONG_NAMES[selectedSong]}
                    </span>
                </div>

                <div className='time-slider-container'>
                    <span className='time-display current'>{formatTime(currentTime)}</span>
                    <input
                        type='range'
                        className='time-slider'
                        min='0'
                        max={duration || 0} // Use duration, default to 0 if NaN/undefined
                        value={currentTime}
                        step='0.1' // Or 1 for whole seconds
                        onChange={handleTimeSliderChange}
                        disabled={!isReadyToPlay || duration === 0} // Disable if not ready or no duration
                        aria-label='Seek slider'
                    />
                    <span className='time-display duration'>{formatTime(duration)}</span>
                </div>

                <div className='tablet-player-buttons'>
                    <button onClick={handlePreviousClick} className='tablet-player-button'><MdSkipPrevious/></button>
                    <button onClick={handlePlayPause} disabled={!isReadyToPlay}
                            className='tablet-player-button'>{isPlaying ? <MdPause/> : <MdPlayArrow/>}</button>
                    <button onClick={handleNextClick} className='tablet-player-button'><MdSkipNext/></button>
                </div>

            </div>
        </div>
    )
}